// ── Layout Constants ──────────────────────────────────────────────────────────
export const MH = 80;
export const MW = 200;
export const VG = 32;
export const HG = 72;
export const PAD = 20;

// ── Helpers ───────────────────────────────────────────────────────────────────
export function nextPow2(n) {
  let p = 2;
  while (p < n) p *= 2;
  return p;
}

export function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export function roundLabel(fromEnd, matchCount) {
  if (fromEnd === 0) return "Final";
  if (fromEnd === 1) return "Semifinal";
  if (fromEnd === 2) return "Quarterfinal";
  return `Round of ${matchCount * 2}`;
}

// ── Seeded positions ──────────────────────────────────────────────────────────
// Generates an ordering of indices so that the highest seeds are
// maximally spread apart. Works on any count that is a power of 2.
// e.g. seedPositions(4) = [0, 3, 1, 2]
export function seedPositions(count) {
  if (count === 1) return [0];
  const prev = seedPositions(count / 2);
  const result = [];
  for (const pos of prev) {
    result.push(pos);
    result.push(count - 1 - pos);
  }
  return result;
}

// ── Build slots (pair-safe) ───────────────────────────────────────────────────
// Works at the PAIR level to guarantee no match pair has two byes.
// A match pair is slots (i*2, i*2+1).
// Uses seedPositions on pairs to distribute bye-pairs evenly.
export function buildSlots(players, size) {
  const numByes = size - players.length;
  const numPairs = size / 2;
  const pairOrder = seedPositions(numPairs);

  // Last numByes pairs in seed order get byes (spreads them evenly)
  const byePairSet = new Set(pairOrder.slice(numPairs - numByes));

  const slots = new Array(size).fill(null);
  let playerIdx = 0;

  for (let pair = 0; pair < numPairs; pair++) {
    const i = pair * 2;
    if (byePairSet.has(pair)) {
      // Bye pair: one real player, one null
      slots[i] = players[playerIdx++];
      slots[i + 1] = null;
    } else {
      // Full pair: two real players
      slots[i] = players[playerIdx++];
      slots[i + 1] = players[playerIdx++];
    }
  }

  return slots;
}

// ── Rounds from slots ─────────────────────────────────────────────────────────
function buildRounds(slots, size) {
  const numRounds = Math.log2(size);
  const rounds = Array.from({ length: numRounds }, (_, r) =>
    Array.from({ length: size >> (r + 1) }, (_, m) => ({
      team1: r === 0 ? slots[m * 2] : null,
      team2: r === 0 ? slots[m * 2 + 1] : null,
      winner: null,
    })),
  );

  // Auto-advance byes
  for (let m = 0; m < rounds[0].length; m++) {
    const match = rounds[0][m];
    if (match.team1 !== null && match.team2 === null) {
      match.winner = match.team1;
      if (numRounds > 1) {
        const nm = m >> 1;
        if (m % 2 === 0) rounds[1][nm].team1 = match.team1;
        else rounds[1][nm].team2 = match.team1;
      }
    } else if (match.team1 === null && match.team2 !== null) {
      match.winner = match.team2;
      if (numRounds > 1) {
        const nm = m >> 1;
        if (m % 2 === 0) rounds[1][nm].team1 = match.team2;
        else rounds[1][nm].team2 = match.team2;
      }
    }
  }

  return rounds;
}

// ── Slot arrays (the seeding) ─────────────────────────────────────────────────
// These produce the size-S slot array (player names, null = bye). Both the
// single- and double-elimination builders consume them, so seeding behaves
// identically across formats.

// Random placement.
export function randomSlots(names) {
  const size = nextPow2(names.length);
  return buildSlots(shuffle([...names]), size);
}

// Top half seeded by rating (maximally spread apart), bottom half randomly
// placed. Guaranteed no bye-vs-bye. Seeded players land on fixed spots, so
// re-shuffling only moves the unseeded (bottom-half) players.
export function seededSlots(rankedPlayers, allNames) {
  if (!rankedPlayers || rankedPlayers.length === 0) return randomSlots(allNames);

  const size = nextPow2(allNames.length);
  const numByes = size - allNames.length;
  const numPairs = size / 2;
  const half = Math.ceil(allNames.length / 2);

  const seededNames = rankedPlayers.slice(0, half).map((p) => p.name);
  const unseededNames = shuffle(
    allNames.filter((n) => !seededNames.includes(n)),
  );

  // Get pair ordering and determine which pairs get byes
  const pairOrder = seedPositions(numPairs);
  const byePairSet = new Set(pairOrder.slice(numPairs - numByes));

  const slots = new Array(size).fill(null);
  let unseededIdx = 0;

  // Place seeded players into pairs using seed order
  // Each seeded player gets the first slot of their assigned pair
  for (let s = 0; s < seededNames.length; s++) {
    const pair = pairOrder[s];
    const i = pair * 2;
    slots[i] = seededNames[s];
    if (!byePairSet.has(pair)) {
      // Full pair — fill second slot with an unseeded player
      slots[i + 1] = unseededNames[unseededIdx++];
    }
    // Bye pair — slots[i+1] stays null
  }

  // Fill any remaining empty pairs with leftover unseeded players
  for (let pair = 0; pair < numPairs; pair++) {
    const i = pair * 2;
    if (slots[i] === null) {
      if (byePairSet.has(pair)) {
        // Remaining bye pair — one unseeded + bye
        slots[i] = unseededNames[unseededIdx++];
        // slots[i+1] stays null
      } else {
        // Remaining full pair — two unseeded players
        slots[i] = unseededNames[unseededIdx++];
        slots[i + 1] = unseededNames[unseededIdx++];
      }
    }
  }

  return slots;
}

// ── Random bracket ────────────────────────────────────────────────────────────
export function createBracket(names) {
  const size = nextPow2(names.length);
  return buildRounds(randomSlots(names), size);
}

// ── Seeded bracket ────────────────────────────────────────────────────────────
export function createSeededBracket(rankedPlayers, allNames) {
  const size = nextPow2(allNames.length);
  return buildRounds(seededSlots(rankedPlayers, allNames), size);
}

// ── Advance Winner ────────────────────────────────────────────────────────────
export function advance(rounds, r, m, winner) {
  const next = rounds.map((round) => round.map((match) => ({ ...match })));
  next[r][m].winner = winner;
  if (r + 1 < next.length) {
    const nm = m >> 1;
    if (m % 2 === 0) next[r + 1][nm].team1 = winner;
    else next[r + 1][nm].team2 = winner;
  }
  return next;
}

// ── Layout Helpers ────────────────────────────────────────────────────────────
export function cY(r, m) {
  if (r === 0) return PAD + m * (MH + VG) + MH / 2;
  return (cY(r - 1, 2 * m) + cY(r - 1, 2 * m + 1)) / 2;
}

export function cX(r) {
  return PAD + r * (MW + HG);
}
